// Player input: keyboard, tilt, touch zones, and standard-mapped gamepads.

import type { CarInput } from '../sim/car';

export type InputMode = 'tilt' | 'keyboard' | 'touch-only' | 'controller';

export class PlayerInput {
  readonly state: CarInput = { steer: 0, brake: 0, nitro: false, throttle: 0 };
  mode: InputMode = 'keyboard';
  hasMotion = false;
  hornPressed = false;
  private keys = new Set<string>();
  private gamepadIndex: number | null = null;
  private gamepadHorn = false;
  private gamepadNitro = false;
  private suspended = false;
  private disposed = false;
  private tiltRaw = 0;
  private tiltZero = 0;
  private tiltSmoothed = 0;
  private touchBrake = false;
  private touchGas = false;
  private touchSteer = 0;
  private listeners: (() => void)[] = [];
  onHorn: (() => void) | null = null;
  onNitro: (() => void) | null = null;

  constructor(private readonly surface: HTMLElement) {
    const kd = (e: KeyboardEvent) => {
      if (this.suspended || e.repeat) return;
      this.keys.add(e.code);
      if (e.code === 'Space') { e.preventDefault(); this.onHorn?.(); }
      if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') this.onNitro?.();
    };
    const ku = (e: KeyboardEvent) => this.keys.delete(e.code);
    window.addEventListener('keydown', kd); window.addEventListener('keyup', ku);
    this.listeners.push(() => { window.removeEventListener('keydown', kd); window.removeEventListener('keyup', ku); });

    // touch zones: hold the right half to drive, hold the left half to brake (in keyboard mode the left half
    // also steers by drag); the horn is a HUD button or Space
    const active = new Map<number, { side: 'L' | 'R'; x0: number }>();
    const reset = () => {
      this.keys.clear(); active.clear();
      this.touchBrake = false; this.touchGas = false; this.touchSteer = 0;
      this.gamepadHorn = false; this.gamepadNitro = false;
      Object.assign(this.state, { steer: 0, brake: 0, throttle: 0, nitro: false });
    };
    const blur = () => { this.suspended = true; reset(); };
    const focus = () => { this.suspended = document.hidden; };
    const visibility = () => { if (document.hidden) blur(); else focus(); };
    window.addEventListener('blur', blur); window.addEventListener('focus', focus);
    document.addEventListener('visibilitychange', visibility);
    this.listeners.push(() => {
      window.removeEventListener('blur', blur); window.removeEventListener('focus', focus);
      document.removeEventListener('visibilitychange', visibility); reset();
    });
    const down = (e: PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      const side = e.clientX > window.innerWidth / 2 ? 'R' : 'L';
      active.set(e.pointerId, { side, x0: e.clientX });
      if (side === 'R') this.touchGas = true; else this.touchBrake = true;
    };
    const up = (e: PointerEvent) => { const a = active.get(e.pointerId); active.delete(e.pointerId); if (a?.side === 'R') this.touchGas = [...active.values()].some((v) => v.side === 'R'); if (a?.side === 'L') { this.touchSteer = 0; this.touchBrake = [...active.values()].some((v) => v.side === 'L'); } };
    const move = (e: PointerEvent) => { const a = active.get(e.pointerId); if (a && a.side === 'L' && !this.hasMotion) this.touchSteer = Math.max(-1, Math.min(1, (e.clientX - a.x0) / 120)); };
    surface.addEventListener('pointerdown', down); surface.addEventListener('pointerup', up); surface.addEventListener('pointercancel', up); surface.addEventListener('pointermove', move);
    this.listeners.push(() => { surface.removeEventListener('pointerdown', down); surface.removeEventListener('pointerup', up); surface.removeEventListener('pointercancel', up); surface.removeEventListener('pointermove', move); });
  }

  /** Ask for motion access (iOS needs a user gesture). Resolves to true when tilt events flow. */
  async enableTilt(): Promise<boolean> {
    const DOE = (window as unknown as { DeviceOrientationEvent?: { requestPermission?: () => Promise<string> } }).DeviceOrientationEvent;
    if (!DOE) return false;
    try {
      if (typeof DOE.requestPermission === 'function') {
        const r = await DOE.requestPermission();
        if (r !== 'granted') return false;
      }
    } catch { return false; }
    return new Promise<boolean>((resolve) => {
      let got = false;
      const handler = (e: DeviceOrientationEvent) => {
        if (e.beta == null && e.gamma == null) return;
        const o = (screen.orientation?.type ?? 'landscape-primary');
        // landscape: rolling the phone like a steering wheel changes beta; portrait: gamma
        let v = 0;
        if (o.startsWith('landscape')) v = (e.beta ?? 0) * (o === 'landscape-secondary' ? -1 : 1);
        else v = (e.gamma ?? 0);
        this.tiltRaw = v;
        if (!got) { got = true; this.hasMotion = true; this.mode = 'tilt'; this.tiltZero = v; resolve(true); }
      };
      window.addEventListener('deviceorientation', handler);
      this.listeners.push(() => window.removeEventListener('deviceorientation', handler));
      setTimeout(() => { if (!got) resolve(false); }, 1500);
    });
  }

  calibrate(): void { this.tiltZero = this.tiltRaw; }

  /** Poll each step: browsers update gamepad snapshots independently of connection events. */
  private readGamepad(): Gamepad | null {
    let pads: (Gamepad | null)[];
    try { pads = Array.from(navigator.getGamepads?.() ?? []); }
    catch { return null; } // Unsupported or blocked by browser permissions policy.
    const supported = pads.filter((p): p is Gamepad => !!p?.connected && p.mapping === 'standard');
    const active = (p: Gamepad) => Math.abs(p.axes[0] ?? 0) > 0.18 ||
      [0, 2, 6, 7, 14, 15].some(i => (p.buttons[i]?.value ?? 0) > 0.1);
    const current = supported.find(p => p.index === this.gamepadIndex);
    const pad = (current && active(current) ? current : supported.find(active)) ?? current ?? supported[0] ?? null;
    if (this.gamepadIndex !== (pad?.index ?? null)) {
      this.gamepadIndex = pad?.index ?? null;
      this.gamepadHorn = false; this.gamepadNitro = false;
    }
    return pad;
  }

  /** Call once per simulation step. */
  update(dt: number): CarInput {
    const s = this.state;
    if (this.disposed || this.suspended || document.hidden) {
      Object.assign(s, { steer: 0, brake: 0, throttle: 0, nitro: false });
      return s;
    }
    const pad = this.readGamepad();
    const button = (index: number) => Math.max(0, Math.min(1, pad?.buttons[index]?.value ?? 0));
    const horn = button(0) > 0.5, nitro = button(2) > 0.5;
    if (horn && !this.gamepadHorn) this.onHorn?.();
    if (nitro && !this.gamepadNitro) this.onNitro?.();
    this.gamepadHorn = horn; this.gamepadNitro = nitro;
    const axis = Math.max(-1, Math.min(1, pad?.axes[0] ?? 0));
    // Rescale outside the dead zone so a full stick still gives full steering.
    const stick = Math.abs(axis) <= 0.18 ? 0 : Math.sign(axis) * Math.min(1, (Math.abs(axis) - 0.18) / (1 - 0.18));
    const dpad = Number(button(15) > 0.5) - Number(button(14) > 0.5);
    const padSteer = dpad || stick;
    const trigger = (index: number) => button(index) <= 0.05 ? 0 : button(index);
    const padBrake = trigger(6), padThrottle = trigger(7);
    if (!pad && this.mode === 'controller') this.mode = this.hasMotion ? 'tilt' : 'keyboard';
    if (padSteer || padBrake || padThrottle || horn || nitro) this.mode = 'controller';
    else if (this.keys.size || this.touchGas || this.touchBrake) this.mode = this.hasMotion ? 'tilt' : 'keyboard';
    let steer = 0;
    if (this.hasMotion && this.mode !== 'controller') {
      const raw = Math.max(-1, Math.min(1, (this.tiltRaw - this.tiltZero) / 28));
      const dead = Math.abs(raw) < 0.06 ? 0 : raw;
      this.tiltSmoothed += (dead - this.tiltSmoothed) * Math.min(1, 14 * dt);
      steer = this.tiltSmoothed;
    } else {
      if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) steer -= 1;
      if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) steer += 1;
      if (!steer) steer = this.touchSteer;
    }
    s.steer = padSteer || steer;
    const braking = this.touchBrake || this.keys.has('KeyS') || this.keys.has('ArrowDown');
    s.brake = braking ? 1 : padBrake;
    s.nitro = nitro || this.keys.has('ShiftLeft') || this.keys.has('ShiftRight');
    s.throttle = this.touchGas || this.keys.has('KeyW') || this.keys.has('ArrowUp') ? 1 : padThrottle;
    return s;
  }

  dispose(): void { this.disposed = true; for (const l of this.listeners) l(); this.listeners = []; }
}
